import React, { useState } from 'react';
import { AlertTriangle, CheckCircle, X, Info, AlertCircle } from 'lucide-react';
import { motion } from 'framer-motion';

interface InlineAlertProps {
  type: 'success' | 'error' | 'warning' | 'info';
  title?: string;
  message: string;
  dismissible?: boolean;
  onDismiss?: () => void;
  className?: string;
}

export const InlineAlert: React.FC<InlineAlertProps> = ({
  type,
  title,
  message,
  dismissible = false,
  onDismiss,
  className = '',
}) => {
  const [isVisible, setIsVisible] = useState(true);

  if (!isVisible) return null;

  const typeConfig = {
    success: {
      icon: CheckCircle,
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200',
      iconColor: 'text-green-500',
      titleColor: 'text-green-800',
      messageColor: 'text-green-700',
      closeColor: 'text-green-500 hover:text-green-600',
      ringColor: 'focus:ring-green-300',
    },
    error: {
      icon: AlertCircle,
      bgColor: 'bg-red-50',
      borderColor: 'border-red-200',
      iconColor: 'text-red-500',
      titleColor: 'text-red-800',
      messageColor: 'text-red-700',
      closeColor: 'text-red-500 hover:text-red-600',
      ringColor: 'focus:ring-red-300',
    },
    warning: {
      icon: AlertTriangle,
      bgColor: 'bg-amber-50',
      borderColor: 'border-amber-200',
      iconColor: 'text-amber-500',
      titleColor: 'text-amber-800',
      messageColor: 'text-amber-700',
      closeColor: 'text-amber-500 hover:text-amber-600',
      ringColor: 'focus:ring-amber-300',
    },
    info: {
      icon: Info,
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      iconColor: 'text-blue-500',
      titleColor: 'text-blue-800',
      messageColor: 'text-blue-700',
      closeColor: 'text-blue-500 hover:text-blue-600',
      ringColor: 'focus:ring-blue-300',
    },
  };

  const config = typeConfig[type];
  const IconComponent = config.icon;

  const handleDismiss = () => {
    setIsVisible(false);
    if (onDismiss) onDismiss();
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 2 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 2 }}
      transition={{ duration: 0.18, ease: 'easeOut' }}
      role='alert'
    >
      <div
        className={`rounded-lg ${config.bgColor} border ${config.borderColor} p-4 shadow-sm ${className}`}
        role='alert'
      >
        <div className='flex items-center'>
          <IconComponent className={`h-5 w-5 mt-0.5 ${config.iconColor}`} aria-hidden='true' />
          <div className='ml-3 flex-1 text-sm'>
            {title && <h3 className={`font-medium ${config.titleColor}`}>{title}</h3>}
            <p className={`${config.messageColor} ${title ? 'mt-1' : ''}`}>{message}</p>
          </div>
          {dismissible && (
            <button
              onClick={handleDismiss}
              className={`ml-2 inline-flex rounded-md p-1.5 ${config.closeColor} bg-transparent hover:bg-black/5 focus:outline-none focus:ring-2 ${config.ringColor} transition`}
              aria-label='Dismiss alert'
            >
              <X className='h-4 w-4' aria-hidden='true' />
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );
};
